/* card-gallery.js - le feuilletage des photos DANS la vignette d'une carte
   produit. Un seul fichier pour la page d'accueil, la grille de collection, la
   recherche et les rails de recommandation: tous dessinent
   snippets/card-product.liquid, qui documente les mesures derriere chaque choix
   ici.

   POURQUOI UN ELEMENT PERSONNALISE ET PAS LE CHARGEUR D'INTENTION DE theme.js.
   Le contrat `data-intent-src` retient le PREMIER clic et le REJOUE sur
   l'element qui porte le selecteur declare. Une bande dont les diapos sont des
   ancres vers la fiche aurait donc vu son premier clic rejoue sur la bande et
   non sur l'ancre: la fiche ne se serait pas ouverte. Un element personnalise
   n'intercepte rien, et il se met a niveau tout seul quand assets/facets.js
   remplace l'innerHTML de la grille apres un filtre, ce qu'un armement par
   noeud ne survit pas.

   CE QUE connectedCallback FAIT, ET SURTOUT CE QU'IL NE FAIT PAS. Il retire
   `data-pp-card-idle` et pose `tabindex="0"`, donc il REVELE les controles et
   ouvre l'arret de tabulation, pour toutes les galeries de la page des que le
   script differe s'execute. Il ne clone AUCUN gabarit: les photos 2..N restent
   dans leurs <template> tant que le client n'a pas agi, ce qui est la contrainte
   dure de ce lot. Mesure a l'appui dans le doc de card-product.liquid:
   `loading="lazy"` sur une diapo seulement decalee vers la droite ne reporte
   rien du tout (40 des 80 images semees demandees au chargement), un <template>
   reporte tout (0 sur 80).

   LE DEFILEMENT N'EST JAMAIS SCRIPTE AVEC UN `behavior`. `scroll-behavior:
   smooth` est declare dans le CSS de la carte, base.css le plafonne sous
   prefers-reduced-motion avec `scroll-behavior: auto !important`, et une option
   `behavior` explicite passerait outre ce plafond (voir
   state/tools/motionscope.mjs). Ce fichier n'affecte donc que scrollLeft. */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('pp-card-gallery')) return;

  var SLIDE = '.product-card__slide';
  var NAV = '[data-pp-card-nav]';

  /* Une trame, pas un delai: un defilement au doigt emet des dizaines
     d'evenements et seule la derniere position compte. */
  function throttled(fn) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        fn();
      });
    };
  }

  class CardGallery extends HTMLElement {
    /* LES ECOUTEURS SUR L'ELEMENT SONT POSES UNE SEULE FOIS DANS SA VIE, pas a
       chaque connexion. L'editeur de theme et assets/facets.js deconnectent et
       reconnectent des noeuds, et un `bind` refait a chaque connexion rend une
       reference NEUVE, que le DOM ne peut pas dedupliquer: le meme survol
       aurait fini par appeler wake() trois fois. Seul l'ecouteur de l'enveloppe
       est repose, parce que l'enveloppe peut, elle, avoir change. */
    connectedCallback() {
      if (!this.bound) {
        this.bound = true;
        this.onScroll = throttled(this.sync.bind(this));
        this.onWake = this.wake.bind(this);
        this.onPress = this.press.bind(this);
        this.onKey = this.key.bind(this);
        /* LES TROIS SIGNAUX D'INTENTION, et aucun n'est le defilement vertical
           de la page. `pointerover` est le survol delibere au bureau,
           `touchstart` est le doigt qui se pose sur la vignette (la bande doit
           etre defilable AVANT que le navigateur ne classe le geste, donc on ne
           peut pas attendre le premier `pointermove`), `focusin` est le clavier
           qui arrive sur la bande. Passifs: aucun des trois n'annule rien. */
        this.addEventListener('pointerover', this.onWake, { passive: true });
        this.addEventListener('touchstart', this.onWake, { passive: true });
        this.addEventListener('focusin', this.onWake);
        this.addEventListener('scroll', this.onScroll, { passive: true });
        this.addEventListener('keydown', this.onKey);
      }

      if (this.wrap) this.wrap.removeEventListener('click', this.onPress);
      this.wrap = this.closest('.product-card__media-wrap');
      if (this.wrap) this.wrap.addEventListener('click', this.onPress);

      this.removeAttribute('data-pp-card-idle');
      this.setAttribute('tabindex', '0');
      this.sync();
    }

    disconnectedCallback() {
      if (this.wrap) this.wrap.removeEventListener('click', this.onPress);
    }

    slides() {
      return this.querySelectorAll(SLIDE);
    }

    /* La position courante, lue de la bande et jamais retenue dans une variable:
       le doigt et la molette la changent sans passer par ce fichier. Math.abs
       parce que scrollLeft est negatif dans une direction droite-a-gauche. */
    index() {
      var w = this.clientWidth;
      if (!w) return 0;
      return Math.round(Math.abs(this.scrollLeft) / w);
    }

    /* Le seul endroit qui clone les gabarits. Rend true la premiere fois, pour
       que l'appelant sache qu'il vient de changer le contenu de la bande. */
    wake() {
      if (this.awake) return false;
      this.awake = true;
      var tpl = this.querySelectorAll('template[data-pp-card-shot]');
      for (var i = 0; i < tpl.length; i += 1) {
        tpl[i].replaceWith(tpl[i].content.cloneNode(true));
      }
      this.sync();
      return true;
    }

    step(dir) {
      this.wake();
      var w = this.clientWidth;
      var n = this.slides().length;
      if (!w || n < 2) return;
      var next = this.index() + dir;
      if (next < 0) next = 0;
      if (next > n - 1) next = n - 1;
      var sign = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.scrollLeft = sign * next * w;
    }

    /* Les fleches sont des soeurs de la bande, jamais des filles d'une ancre, et
       c'est ce qui dispense d'arreter une navigation: rien a annuler, parce que
       rien ne navigue. stopPropagation reste pose parce qu'une carte de la page
       d'accueil vit dans une <pp-slider> qui ecoute les pointeurs de sa piste,
       et qu'une fleche n'a pas a lui ressembler a un glissement. */
    press(event) {
      var nav = event.target.closest ? event.target.closest(NAV) : null;
      if (!nav || !this.wrap || !this.wrap.contains(nav)) return;
      event.preventDefault();
      event.stopPropagation();
      this.step(Number(nav.getAttribute('data-pp-card-nav')) || 1);
    }

    /* LE CLAVIER EST TRAITE ICI ET PAS LAISSE AU NAVIGATEUR, et ce n'est pas de
       la defiance: une fleche pressee sur un conteneur qui defile deplace la
       position d'une quarantaine de pixels, et `scroll-snap-type: x mandatory`
       ramene alors la bande sur la diapo d'ou elle vient. La presse aurait donc
       paru ne rien faire. On avance d'une diapo entiere, et on annule le defaut
       pour que la page ne descende pas sous le doigt. */
    key(event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      /* Reveiller AVANT de compter: sans cela la bande ne porte qu'une diapo au
         moment ou Home et End lisent leur borne, et les deux se seraient
         contentees d'un pas de un. */
      this.wake();
      var n = this.slides().length;
      if (event.key === 'ArrowRight') this.step(1);
      else if (event.key === 'ArrowLeft') this.step(-1);
      else if (event.key === 'Home') this.step(-n);
      else if (event.key === 'End') this.step(n);
      else return;
      event.preventDefault();
    }

    /* L'ETAT VISIBLE ET L'ETAT DIT, ecrits au meme endroit pour qu'ils ne
       puissent pas diverger. Les points sont aria-hidden (le groupe a cote
       porte deja le nom du produit), la ligne role="status" est la moitie
       parlee, et son gabarit vient de la meme cle de traduction que la galerie
       de la fiche produit, avec la meme convention a accolade simple. */
    sync() {
      if (!this.wrap) return;
      var i = this.index();
      /* LE TOTAL COMPTE LES GABARITS ENCORE FERMES, et c'est le correctif d'un
         defaut de ce fichier: avant le reveil la bande ne porte qu'UNE diapo,
         donc une premiere version annoncait "Image 1 of 1" au focus sur une
         carte qui en a six, et desactivait la fleche avant sur toutes les
         cartes de la page. */
      var total = this.slides().length + this.querySelectorAll('template[data-pp-card-shot]').length;
      var n = total;

      var dots = this.wrap.querySelectorAll('.product-card__dot');
      for (var d = 0; d < dots.length; d += 1) {
        dots[d].classList.toggle('product-card__dot--on', d === i);
      }

      var status = this.wrap.querySelector('[data-pp-card-status]');
      var label = this.getAttribute('data-label-status');
      if (status && label) {
        status.textContent = label.replace('{index}', String(i + 1)).replace('{count}', String(n));
      }

      var navs = this.wrap.querySelectorAll(NAV);
      for (var k = 0; k < navs.length; k += 1) {
        var dir = Number(navs[k].getAttribute('data-pp-card-nav')) || 1;
        navs[k].disabled = dir < 0 ? i <= 0 : i >= n - 1;
      }
    }
  }

  window.customElements.define('pp-card-gallery', CardGallery);
})();
